import type React from 'react'

import { type PayrollPayScheduleSource } from 'utils/web_api.schemas'

import { type InstructionsProps } from '../components/PayrollProviderInstructions'

export const PayFrequencies = {
  Monthly: 'monthly',
  SemiMonthly: 'semi_monthly',
  BiWeekly: 'bi_weekly',
  Weekly: 'weekly'
} as const

export type PayFrequency = typeof PayFrequencies[keyof typeof PayFrequencies]

export const PayFrequencyLabels = {
  [PayFrequencies.Monthly]: 'Monthly',
  [PayFrequencies.SemiMonthly]: 'Semi-Monthly',
  [PayFrequencies.BiWeekly]: 'Bi-Weekly',
  [PayFrequencies.Weekly]: 'Weekly'
}

export const BankClosureStrategies = {
  Before: 'before',
  After: 'after',
  Closest: 'closest',
  Ignore: 'ignore'
} as const

export type BankClosureStrategy = typeof BankClosureStrategies[keyof typeof BankClosureStrategies]

export const BankClosureStrategyLabels: Record<BankClosureStrategy, string> = {
  before: 'Previous business day',
  after: 'Next business day',
  closest: 'Nearest business day',
  ignore: 'Ignore bank closures'
} as const

export type PaySchedule = {
  id: string
  name: string
  frequency: PayFrequency
  reference_pay_date?: string
  start_date: string
  end_date?: string | null
  first_day?: number
  second_day?: number
  bank_closure_strategy: BankClosureStrategy
  day_pattern?: 'first_day' | 'last_day'
  pay_before_bank_closure?: boolean
  source: PayrollPayScheduleSource
  is_verified: boolean
  archived: boolean
}

export type PayPeriodStatus = 'pending' | 'processing' | 'completed' | 'failed'

export type PayPeriod = {
  id: string
  end_date: string
  pay_date: string
  deductions_available_on: string
  status: PayPeriodStatus
  start_date?: string
}

export type Provider = {
  id: string;
  name: string;
  component: React.ComponentType<InstructionsProps>;
}

export type GetSamplePayPeriodsParams = {
  frequency: PayFrequency
  bank_closure_strategy: BankClosureStrategy
  reference_pay_date?: string
  first_day?: number
  second_day?: number
  isValid: boolean
  start_date?: string
  end_date?: string | null
}

export type GeneratePayScheduleFormProps = Omit<GetSamplePayPeriodsParams, 'isValid' | 'isValidating'> & {
  name: string
}

export type PayScheduleFormInputs = {
  frequency: PayFrequency
  name: string
  bankClosureStrategy: BankClosureStrategy
  startDate: string
  endDate?: string | null
  firstDay?: number
  secondDay?: number
  examplePayDate?: string
}

export type PayrollConfig = {
  id: string
  connected_to_payroll: boolean
  provider_display_name: string | null
  provider_icon_url: string | null
  provider_api_connection_method: string | null
  provider_config_id: string | null
  last_employee_sync: string | null
  next_employee_sync: string | null
  has_payroll_integration: boolean
  supports_automatic_deductions: boolean
  automatic_deductions_setup_complete: boolean
  automatic_deductions_enabled: boolean
  requires_employee_deduplication: boolean
  has_invited_manually_added_employees: boolean
  show_automated_deductions_notice: boolean
  hide_payroll_management: boolean
}

export type PayrollProviderConfig = {
  id: string
  name: string
  icon_image_url: string
  logo_image_url: string
  api_connection_remote_id: string
  api_connection_method: string
  supports_automatic_deductions?: boolean
}
